// Viewport maths: CSS size → logical size/viewScale, and applying that to
// the renderer and the DOM's type scale. Pure functions of their arguments
// (plus `window`/`document` reads for the ambient CSS size) — no state kept
// here; callers hold the result.
import { viewScale } from '../sim/index.ts';
import type { Renderer } from '../render/canvas.ts';
import { relayout } from './game.ts';
import type { Session } from './session.ts';

export interface Viewport {
  cssW: number;
  cssH: number;
  vs: number;
  dpr: number;
  logicalW: number;
  logicalH: number;
}

export function measure(): { cssW: number; cssH: number } {
  return { cssW: window.innerWidth, cssH: window.innerHeight };
}

export function computeViewport(): Viewport {
  const { cssW, cssH } = measure();
  const vs = viewScale(cssW, cssH);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  return { cssW, cssH, vs, dpr, logicalW: cssW / vs, logicalH: cssH / vs };
}

// Tablet type scale: the HUD/legend/veil are plain CSS/HTML, sized in fixed
// logical px, so they don't get bigger the way canvas-drawn content does when
// `viewScale` grows on a large screen. `--ts` (read by index.html's typography
// rules) mirrors `viewScale` up to a cap, so an iPad reads proportionally like
// an iPhone without the type getting comically large.
const TYPE_SCALE_CAP = 1.5;
export function applyTypeScale(viewScaleNow: number): void {
  document.documentElement.style.setProperty('--ts', String(Math.min(viewScaleNow, TYPE_SCALE_CAP)));
}

export function applyRendererSize(renderer: Renderer, session: Session, v: Viewport): void {
  renderer.resize(v.cssW, v.cssH, v.dpr, v.vs);
  session.vs = v.vs;
  applyTypeScale(v.vs);
}

export function onResize(renderer: Renderer, session: Session): void {
  const v = computeViewport();
  applyRendererSize(renderer, session, v);
  if (session.world) relayout(session.world, v.logicalW, v.logicalH);
}
